import {
  CalendarOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  OrderedListOutlined,
  PlusOutlined,
  SendOutlined,
  SlidersOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  message,
  Typography,
} from "antd";
import "./TermsRequestPanel.css";
import "../../../../styles/GlobalStyle.css";
import { ReactComponent as ChatSendIcon } from "../../../../assests/chat-send-icon.svg";
import {
  sendMessage,
  sendTermsRequest,
} from "../../../../components/common/utils/WebSocket";
import { useDispatch, useSelector } from "react-redux";
import {
  defaultTermsBookingStatus,
  getCampaignsForPurchase,
} from "../../../../store/thunks/buyer-thunks/BuyerThunks";
import { formatToUSCurrency } from "../../../../components/common/utils/Utils";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
const TermsRequestPanel = ({
  selected,
  termsType,
  setTermsType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  installments,
  setInstallments,
  termsNote,
  setTermsNote,
  setIsTermsRequestOpen,
  setOptimisticTermsMessage,
  allProductsApproved,
  selectedProductApproved,
  approvedData,
  selectedCampaign,
  receiverUserId,
  requestType,
}) => {
  const totalAmount = selected?.originalProduct?.offerpriceperselectedDates;
  const Text = Typography;
  const allocatedPercentage = installments.reduce(
    (sum, item) => sum + (Number(item.percentage) || 0),
    0,
  );
  const [activeTab, setActiveTab] = useState("edit");
  const userRole = useSelector((state) => state.login.userType);
  const isSeller = userRole !== "basic";
  const buyerPaymentTerms = approvedData?.paymentTerms;
  const hasBuyerProposal =
    buyerPaymentTerms?.calendarData?.length > 0 &&
    buyerPaymentTerms?.percentageData?.length > 0;

  const initializedBuyerTermsRef = useRef(null);

  useEffect(() => {
    const calendarData = buyerPaymentTerms?.calendarData || [];
    const percentageData = buyerPaymentTerms?.percentageData || [];

    if (!calendarData.length || !percentageData.length) return;

    const proposalKey = JSON.stringify({
      calendarData,
      percentageData,
    });

    if (initializedBuyerTermsRef.current === proposalKey) return;
    initializedBuyerTermsRef.current = proposalKey;
    const buyerInstallments = calendarData.map((date, index) => ({
      id: `buyer-${index}-${date}`,
      name: `Payment ${index + 1}`,
      date: date ? dayjs(date) : null,
      percentage: Number(percentageData[index]) || 0,
    }));

    setInstallments(buyerInstallments);
  }, [
    userRole,
    buyerPaymentTerms?.calendarData,
    buyerPaymentTerms?.percentageData,
    setInstallments,
  ]);
  const allocatedAmount = (totalAmount * allocatedPercentage) / 100;
  const remainingPercentage = Math.max(0, 100 - allocatedPercentage);
  const allInstallmentsComplete = installments.every(
    (item) => item?.date && Number(item?.percentage) > 0,
  );
  const isCustomTermsValid =
    allocatedPercentage === 100 && allInstallmentsComplete;
  const lastInstallment = installments[installments.length - 1];
  const isCurrentInstallmentComplete =
    lastInstallment?.date && Number(lastInstallment?.percentage) > 0;
  const isAddPaymentDisabled =
    !isCurrentInstallmentComplete || allocatedPercentage >= 100;
  const addInstallment = () => {
    setInstallments((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: `Payment`,
        date: null,
        percentage: 0,
      },
    ]);
  };
  const removeInstallment = (id) => {
    setInstallments((prev) => prev.filter((item) => item.id !== id));
  };
  const updateInstallment = (id, field, value) => {
    setInstallments((prev) =>
      prev?.map((item) =>
        item?.id === id
          ? {
              ...item,
              [field]: value,
            }
          : item,
      ),
    );
  };
  const resetTermsForm = () => {
    setInstallments((prev) => {
      if (!prev.length) {
        return [
          {
            id: Date.now(),
            name: "Payment",
            date: null,
            percentage: 0,
          },
        ];
      }
      return [
        {
          ...prev[0],
          date: null,
          percentage: 0,
        },
      ];
    });

    setTermsNote("");
    setIsTermsRequestOpen(false);
  };
  const { userId } = useSelector((state) => state.login);
  const { clientId } = useSelector((state) => state.userprofile);
  let socketId = userRole === "basic" ? userId : clientId;
  const { isProductInfo, campaignInfoForChat, isChatModal } = useSelector(
    (state) => state.chatCommunicationHandleSlice,
  );
  const productId =
    selected?.originalProduct?.product_id ||
    selected?.originalProduct?._id ||
    selected?.productId ||
    "";
  const bookingId =
    selected?.bookingId || selected?.originalProduct?.productbookingid;
  const campaignId = selected?.campaignId || selectedCampaign?.campaignId;
  const dispatch = useDispatch();
  const { chatLoading, productsChatHistory } = useSelector(
    (state) => state.chatHistory,
  );
  const handleTermsRequest = (termsType) => {
    // Keep existing default-term flow untouched
    if (termsType === "default" && !isSeller) {
      const payload = {
        terms_status: selectedProductApproved ? 1 : 2,
        product_booking_id: selected?.originalProduct?.productbookingid,
      };
      dispatch(defaultTermsBookingStatus(payload)).then((res) => {
        if (res?.payload?.status == 1) {
          dispatch(getCampaignsForPurchase());
        }
      });
      return;
    }
    const resolvedRequestType = isSeller
      ? "counter_terms_request"
      : requestType === "counter_terms_request"
        ? "counter_terms_request"
        : "initial_terms_request";
    const selectedProductHistory =
      productsChatHistory?.find(
        (history) =>
          String(history?.bookingId) === String(bookingId) &&
          String(history?.productId) === String(productId),
      ) || null;
    const toUserId = isSeller
      ? selectedProductHistory?.message_sender_id
      : receiverUserId;
    if (!socketId) {
      console.error("Counter Request: Missing fromUserId", {
        socketId,
        userRole,
      });
      return;
    }
    if (!toUserId) {
      console.error("Counter Request: Missing toUserId", {
        userRole,
        receiverUserId,
        messageSenderId: selectedProductHistory?.message_sender_id,
        bookingId,
        productId,
        campaignId,
        selectedProductHistory,
      });
      message.error("Something went wrong. Please try again.");
      return;
    }
    if (!productId || !bookingId || !campaignId) {
      console.error("Counter Request: Missing identifiers", {
        productId,
        bookingId,
        campaignId,
      });
      message.error(
        "Unable to send the counter request. Required information is missing.",
      );
      return;
    }
    const calendarData = installments.map((item) =>
      item?.date ? item.date.format("YYYY-MM-DD") : "",
    );
    const percentageData = installments.map((item) =>
      String(item?.percentage || 0),
    );
    const termsData = {
      request_type: resolvedRequestType,
      calendar_data: calendarData,
      percentage_data: percentageData,
      ...(termsNote?.trim() && {
        comment_data: termsNote.trim(),
      }),
    };
    const termsMessage = JSON.stringify(termsData);
    const optimisticId = `terms-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticId,
      messageId: optimisticId,
      text: termsMessage,
      sender: "sender",
      createdAt: Date.now(),
      productId,
      campaignId,
      bookingId,
      chat_group_id: selectedProductHistory?.chat_group_id || null,
      terms_data: termsData,
      isOptimistic: true,
    };
    try {
      sendMessage({
        fromUserId: socketId,
        toUserId,
        campaignId,
        productId,
        bookingId,
        message: termsMessage,
      });
      setOptimisticTermsMessage(optimisticMessage);
      setIsTermsRequestOpen(false);
    } catch (error) {
      console.error("COUNTER REQUEST WEBSOCKET FAILED", error);
      message.error("Counter request could not be sent. Please try again.");
    }
  };
  const disabledCampaignDate = (current, installmentIndex) => {
    if (!current || !selectedCampaign?.campaign_dates) {
      return false;
    }
    const campaignStart = dayjs(
      selectedCampaign.campaign_dates.campaign_start_date,
    ).startOf("day");
    const campaignEnd = dayjs(
      selectedCampaign?.campaign_dates?.campaign_end_date,
    ).endOf("day");
    // 1. Disable dates outside campaign start and end
    if (
      current.isBefore(campaignStart, "day") ||
      current.isAfter(campaignEnd, "day")
    ) {
      return true;
    }

    // 2. Disable dates selected in previous installments
    const previousSelectedDates = installments
      .slice(0, installmentIndex)
      ?.map((item) => item?.date)
      ?.filter(Boolean);

    return previousSelectedDates.some((date) =>
      current.isSame(dayjs(date), "day"),
    );
  };
  const isDefaultTerms = termsType === "default";

  const disableTermsButton =
    (!isDefaultTerms && selectedProductApproved) ||
    (termsType === "custom" && !isCustomTermsValid) ||
    (isSeller &&
      (!allInstallmentsComplete || Number(allocatedPercentage) !== 100));
  const getPaymentTerm = (product) => {
    if (Number(product?.net_7_percent) > 0) {
      return {
        label: "Weekly",
        percent: Number(product?.net_7_percent),
      };
    }
    if (Number(product?.net_30_percent) > 0) {
      return {
        label: "Monthly",
        percent: Number(product?.net_30_percent),
      };
    }
    if (Number(product?.net_90_percent) > 0) {
      return {
        label: "Quarterly",
        percent: Number(product?.net_90_percent),
      };
    }
    return null;
  };
  const calculatePercentageAmount = (total, percentage) => {
    const totalAmount = Number(total) || 0;
    const percent = Number(percentage) || 0;
    return (totalAmount * percent) / 100;
  };
  const product = selected?.originalProduct;
  const totalPrice = Number(product?.offerpriceperselectedDates) || 0;
  const upfrontPercent = Number(product?.stripe_percent) || 0;
  const upfrontAmount = calculatePercentageAmount(totalPrice, upfrontPercent);
  const paymentTerm = getPaymentTerm(product);
  const remainingAmount = paymentTerm
    ? calculatePercentageAmount(totalPrice, paymentTerm?.percent)
    : 0;
  return (
    <div className="messages-terms-request-wrapper">
      <div className="messages-terms-request-header">
        <div className="messages-terms-request-header-left">
          <div>
            <Text className="common-text-1E2939-14-500">
              {isSeller
                ? "Send Counter-Offer"
                : requestType === "counter_terms_request"
                  ? "Make a Counter Terms Request"
                  : "Make a Terms Request"}
            </Text>

            <Text className="location-grey-text-12">
              {isSeller ? `to ${"Buyer"}` : "to Seller"}
            </Text>
          </div>
        </div>

        <CloseOutlined
          className="messages-terms-close"
          onClick={resetTermsForm}
        />
      </div>
      {isSeller && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "14px 16px",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div className="edit-preview-toggle">
            <button
              type="button"
              className={`toggle-btn ${activeTab === "edit" ? "active" : ""}`}
              onClick={() => setActiveTab("edit")}
            >
              Edit Terms
            </button>

            <button
              type="button"
              className={`toggle-btn ${activeTab === "preview" ? "active" : ""}`}
              onClick={() => setActiveTab("preview")}
            >
              Preview
            </button>
          </div>
          <div className="messages-terms-request-body counter-offer-container">
            {activeTab == "edit" ? (
              <>
                <div className="messages-terms-product-card">
                  <Text className="location-grey-text-12">PRODUCT</Text>
                  <Text className="common-text-101828-14 truncate">
                    {selected?.title}
                  </Text>

                  <Text className="location-grey-text-12 truncate">
                    {selected?.originalProduct?.city}
                  </Text>
                </div>
                <div className="messages-terms-product-card">
                  <Text className="location-grey-text-12">
                    Total Amount (fixed)
                  </Text>
                  <Text className="terms-chat-total-amount">
                    {formatToUSCurrency(
                      selected?.originalProduct?.offerpriceperselectedDates,
                    )}
                  </Text>

                  <Text
                    className="location-grey-text-12 buyer-proposed-terms"
                    style={{ marginTop: "10px" }}
                  >
                    Buyer proposed:{" "}
                    {approvedData?.paymentTerms?.percentageData?.length > 0
                      ? approvedData.paymentTerms.percentageData.map(
                          (percentage, index) => {
                            const amount =
                              (Number(totalAmount) * Number(percentage)) / 100;

                            const date =
                              approvedData?.paymentTerms?.calendarData?.[index];

                            const formattedDate = date
                              ? new Date(date).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric",
                                })
                              : "-";

                            return (
                              <span
                                key={`${date}-${index}`}
                                className="buyer-proposed-term-line"
                              >
                                <strong>{formattedDate}</strong>
                                {" · "}
                                <strong>
                                  {formatToUSCurrency(amount)}
                                </strong>{" "}
                                <strong>({percentage}%)</strong>
                              </span>
                            );
                          },
                        )
                      : " -"}
                  </Text>
                </div>

                {isSeller && (
                  <div className="seller-payment-schedule">
                    <div className="seller-payment-schedule-header">
                      <Text className="common-action-items-text-grey ">
                        Payment Schedule
                      </Text>
                      <button
                        type="button"
                        className="seller-payment-add-btn"
                        onClick={addInstallment}
                        disabled={isAddPaymentDisabled}
                      >
                        <PlusOutlined />
                        Add
                      </button>
                    </div>

                    <div className="seller-payment-schedule-list">
                      {installments?.map((item, index) => {
                        const percentage = Number(item.percentage) || 0;
                        const amount = (totalAmount * percentage) / 100;
                        const isIncompleteAllocation =
                          percentage > 0 && allocatedPercentage < 100;
                        return (
                          <div className="seller-payment-row" key={item.id}>
                            <div className="seller-payment-date">
                              <DatePicker
                                value={item?.date}
                                onChange={(date) =>
                                  updateInstallment(item.id, "date", date)
                                }
                                disabledDate={(current) =>
                                  disabledCampaignDate(current, index)
                                }
                                format="MM-DD-YYYY"
                                placeholder="dd-mm-yyyy"
                                allowClear
                              />
                            </div>
                            <div
                              className={`seller-payment-percentage ${
                                isIncompleteAllocation
                                  ? "seller-payment-percentage-warning"
                                  : ""
                              }`}
                            >
                              <InputNumber
                                min={0}
                                max={Math.min(
                                  100,
                                  100 -
                                    allocatedPercentage +
                                    (Number(percentage) || 0),
                                )}
                                value={percentage}
                                controls
                                precision={0}
                                onFocus={(e) => {
                                  if (Number(percentage) === 0) {
                                    e.target.select();
                                  }
                                }}
                                onKeyDown={(e) => {
                                  const allowedKeys = [
                                    "Backspace",
                                    "Delete",
                                    "ArrowLeft",
                                    "ArrowRight",
                                    "Tab",
                                    "Home",
                                    "End",
                                  ];

                                  if (
                                    !/[0-9]/.test(e.key) &&
                                    !allowedKeys.includes(e.key) &&
                                    !(e.ctrlKey || e.metaKey)
                                  ) {
                                    e.preventDefault();
                                  }
                                }}
                                parser={(value) =>
                                  value?.replace(/[^\d]/g, "") || ""
                                }
                                formatter={(value) => value ?? ""}
                                onChange={(value) =>
                                  updateInstallment(
                                    item.id,
                                    "percentage",
                                    value ?? 0,
                                  )
                                }
                              />
                              <span>%</span>
                            </div>
                            <div className="seller-payment-amount common-text-99a1af-12">
                              {percentage > 0
                                ? formatToUSCurrency(amount)
                                : "—"}
                            </div>
                            <div className="seller-payment-delete">
                              {installments.length > 1 && (
                                <CloseOutlined
                                  onClick={() => removeInstallment(item.id)}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div
                      className={`seller-payment-total ${
                        allocatedPercentage === 100 && allInstallmentsComplete
                          ? "seller-payment-total-complete"
                          : ""
                      }`}
                    >
                      <span>Total — {formatToUSCurrency(totalAmount)}</span>
                      {allocatedPercentage === 100 &&
                      allInstallmentsComplete ? (
                        <span>
                          100%
                          <span className="seller-payment-check">✓</span>
                        </span>
                      ) : (
                        <span>
                          {allocatedPercentage}% · {remainingPercentage}%
                          unallocated
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <div
                  className="messages-terms-form-section "
                  style={{ marginTop: "10px" }}
                >
                  <Text className="common-text-101828-14 ">
                    Note to Buyer (optional){" "}
                  </Text>
                  <Input.TextArea
                    value={termsNote}
                    onChange={(e) => setTermsNote(e.target.value)}
                    rows={3}
                    maxLength={100}
                    placeholder="e.g. We'd like 50% upfront and the rest on delivery..."
                    className="messages-terms-note-input"
                  />
                </div>
              </>
            ) : (
              <div className="messages-terms-preview">
                <div className="messages-terms-preview-hint">
                  <span className="messages-terms-preview-hint-icon">
                    <EyeOutlined />
                  </span>

                  <span>How the buyer will see this</span>
                </div>

                <div className="messages-terms-preview-card">
                  <div className="messages-terms-preview-header">
                    <div className="messages-terms-preview-title">
                      <span className="messages-terms-preview-icon">
                        {" "}
                        <SyncOutlined />
                      </span>

                      <span>PAYMENT SCHEDULE FROM SELLER</span>
                    </div>
                  </div>

                  <div className="messages-terms-preview-payments">
                    {installments
                      ?.filter(
                        (item) => item?.date && Number(item?.percentage) > 0,
                      )
                      ?.map((item) => {
                        const percentage = Number(item.percentage) || 0;
                        const amount = (totalAmount * percentage) / 100;
                        return (
                          <div
                            className="messages-terms-preview-payment"
                            key={item?.id}
                          >
                            <span>{item?.date?.format("YYYY-MM-DD")}</span>
                            <span>
                              {formatToUSCurrency(amount)} ({percentage}%)
                            </span>
                          </div>
                        );
                      })}
                  </div>

                  <div className="messages-terms-preview-total">
                    <span>Total Due</span>
                    <span>
                      {allocatedAmount != null && allocatedPercentage != null
                        ? `${formatToUSCurrency(allocatedAmount)} (${allocatedPercentage}%)`
                        : "-"}
                    </span>
                  </div>
                </div>

                <div className="messages-terms-preview-proposed">
                  <div className="common-text-101828-12">
                    Buyer's proposed schedule:
                  </div>

                  <div className="location-grey-text-12">Net 30</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {!isSeller && (
        <>
          <div className="messages-terms-tabs">
            <div
              className={`messages-terms-tab ${
                termsType === "default"
                  ? "messages-terms-tab-active common-text-color-red-12-400"
                  : ""
              }`}
              onClick={() => {
                if (selectedProductApproved) return;
                setTermsType("default");
              }}
            >
              <OrderedListOutlined />
              <span>Default Terms</span>
            </div>

            <div
              className={`messages-terms-tab ${
                termsType === "custom"
                  ? "messages-terms-tab-active common-text-color-red-12-400"
                  : ""
              } ${selectedProductApproved ? "disabled-opacity" : ""}`}
              onClick={() => {
                if (selectedProductApproved) return;
                setTermsType("custom");
              }}
            >
              <SlidersOutlined />
              <span>Custom Terms</span>
            </div>
          </div>
          <div className="messages-terms-request-body">
            <div
              className="messages-terms-product-card"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <Text className="location-grey-text-12">PRODUCT</Text>
              <Text className="common-text-101828-14 truncate">
                {selected?.title}
              </Text>

              <Text className="location-grey-text-12 truncate">
                {selected?.originalProduct?.city}
              </Text>
            </div>
            {termsType === "default" ? (
              <>
                <div className="messages-terms-section-card">
                  <div className="messages-terms-section-header">
                    <Text className="common-text-101828-14">
                      SELLER'S LISTED TERMS
                    </Text>
                  </div>

                  <div className="messages-terms-info-row">
                    <Text className="location-grey-text-12">Product Price</Text>
                    <Text className="common-text-101828-14">
                      {" "}
                      {formatToUSCurrency(
                        selected?.originalProduct?.offerpriceperselectedDates,
                      )}
                    </Text>
                  </div>

                  <div className="messages-terms-info-row">
                    <Text className="location-grey-text-12">
                      Upfront Amount
                    </Text>
                    <Text className="common-text-101828-14">
                      {formatToUSCurrency(upfrontAmount)}

                      {paymentTerm && (
                        <span className="location-grey-text-12">
                          {" "}
                          ({upfrontPercent}%)
                        </span>
                      )}
                    </Text>
                  </div>

                  <div className="messages-terms-info-row ">
                    <Text className="location-grey-text-12">Remaining</Text>
                    <Text className="common-text-101828-14">
                      {formatToUSCurrency(remainingAmount)}

                      {paymentTerm && (
                        <span className="location-grey-text-12">
                          {" "}
                          ({paymentTerm?.label})
                        </span>
                      )}
                    </Text>
                  </div>
                </div>
                <div className="messages-terms-info-box">
                  <Text className="location-grey-text-12 color-text">
                    Requesting default terms means you accept the seller's
                    standard payment structure at the listed price.
                  </Text>
                </div>
              </>
            ) : (
              <>
                <div className="messages-terms-request-card">
                  <div className="messages-terms-request-total">
                    <div style={{ width: "60%" }}>
                      <Text className="location-grey-text-12">
                        REQUESTING TERMS FOR
                      </Text>

                      <Text className="common-text-101828-14 truncates">
                        {selected?.title}
                      </Text>
                    </div>
                    <div className="messages-terms-total">
                      <Text className="location-grey-text-12">Total</Text>
                      <Text className="common-text-101828-14 ">
                        {formatToUSCurrency(
                          selected?.originalProduct?.offerpriceperselectedDates,
                        )}
                      </Text>
                    </div>
                  </div>
                  <div className="messages-terms-form-section padding-styles">
                    <div className="messages-terms-payment-header">
                      <Text className="common-text-101828-14 ">
                        Payment Schedule
                      </Text>
                      <Text className="location-grey-text-12">
                        {" "}
                        {formatToUSCurrency(
                          selected?.originalProduct?.offerpriceperselectedDates,
                        )}
                      </Text>
                    </div>
                    <div className="messages-terms-installment-heading">
                      <span className="installment-col-name">Installment</span>
                      <span className="installment-col-date">Date</span>
                      <span className="installment-col-payment">
                        % · Amount
                      </span>
                      <span className="installment-col-delete"></span>
                    </div>
                    {installments?.map((item, index) => (
                      <div
                        className="messages-terms-installment-row"
                        key={item?.id}
                      >
                        <div className="messages-installment-name">
                          <span className="messages-installment-number">
                            {index + 1}
                          </span>
                          <Text className="common-text-101828-12">
                            {item?.name}
                          </Text>
                        </div>
                        <div className="messages-installment-date">
                          <DatePicker
                            value={item?.date}
                            onChange={(date) =>
                              updateInstallment(item?.id, "date", date)
                            }
                            disabledDate={(current) =>
                              disabledCampaignDate(current, index)
                            }
                            format="MM-DD-YYYY"
                            placeholder="dd-mm-yyyy"
                          />
                        </div>
                        <div className="messages-installment-percentage">
                          <div className="messages-installment-percent-input">
                            <InputNumber
                              min={0}
                              max={Math.min(
                                100,
                                100 -
                                  allocatedPercentage +
                                  (Number(item.percentage) || 0),
                              )}
                              value={item.percentage}
                              controls
                              precision={0}
                              onFocus={(e) => {
                                if (Number(item.percentage) === 0) {
                                  e.target.select();
                                }
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = [
                                  "Backspace",
                                  "Delete",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Tab",
                                  "Home",
                                  "End",
                                ];

                                if (
                                  !/[0-9]/.test(e.key) &&
                                  !allowedKeys.includes(e.key) &&
                                  !(e.ctrlKey || e.metaKey)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              parser={(value) =>
                                value?.replace(/[^\d]/g, "") || ""
                              }
                              formatter={(value) => value ?? ""}
                              onChange={(value) =>
                                updateInstallment(
                                  item.id,
                                  "percentage",
                                  value ?? 0,
                                )
                              }
                            />

                            <span className="messages-installment-percent-symbol">
                              %
                            </span>
                          </div>

                          <div className="messages-installment-amount">
                            {Number(item.percentage) > 0
                              ? `$${(
                                  (totalAmount *
                                    (Number(item.percentage) || 0)) /
                                  100
                                ).toLocaleString()}`
                              : "—"}
                          </div>
                        </div>
                        <div className="messages-installment-delete-wrapper">
                          {index > 0 && (
                            <CloseOutlined
                              className="messages-installment-delete"
                              onClick={() => removeInstallment(item.id)}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    <Button
                      type="default"
                      block
                      icon={<PlusOutlined />}
                      className={`messages-add-installment-button ${
                        isAddPaymentDisabled ? "disable-opacity" : ""
                      }`}
                      onClick={addInstallment}
                      disabled={isAddPaymentDisabled}
                    >
                      Add Payment
                    </Button>
                  </div>
                </div>
                <div className="messages-terms-form-section margin-styles">
                  <Text className="common-text-101828-14 ">
                    Note{" "}
                    <span className="location-grey-text-12">(optional)</span>
                  </Text>
                  <Input.TextArea
                    value={termsNote}
                    onChange={(e) => setTermsNote(e.target.value)}
                    rows={3}
                    maxLength={100}
                    placeholder="e.g. We'd like 50% upfront and the rest on delivery..."
                    className="messages-terms-note-input"
                  />
                </div>
                <div
                  className={`messages-terms-allocation ${
                    allocatedPercentage >= 100
                      ? "messages-terms-allocation-complete"
                      : ""
                  }`}
                >
                  <div className="messages-terms-allocation-info">
                    <Text className="location-grey-text-12">
                      {allocatedPercentage >= 100
                        ? "✓ 100% allocated — ready to send"
                        : `${allocatedPercentage.toFixed(1)}% allocated · ${remainingPercentage.toFixed(1)}% remaining`}
                    </Text>

                    <Text className="common-text-101828-12-500">
                      ${allocatedAmount.toLocaleString()} / $
                      {totalAmount.toLocaleString()}
                    </Text>
                  </div>

                  <div className="messages-terms-allocation-progress">
                    <div
                      className="messages-terms-allocation-progress-fill"
                      style={{
                        width: `${Math.min(100, Math.max(0, allocatedPercentage))}%`,
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <div className="messages-terms-request-footer alignment-footer">
        {termsType === "custom" && remainingPercentage > 0 && (
          <Text className="location-grey-text-12">
            Allocate all 100.0% remaining to enable sending
          </Text>
        )}
        <Button
          className={`common-button-red messages-send-terms-button common-text-white-14 ${
            disableTermsButton ? "disabled-opacity" : ""
          }`}
          disabled={disableTermsButton}
          onClick={() => {
            handleTermsRequest(termsType);
          }}
        >
          <ChatSendIcon style={{ cursor: "pointer" }} />

          {isSeller
            ? "Send Payment Schedule"
            : termsType === "default"
              ? selectedProductApproved
                ? "Remove Default Terms"
                : "Accept Default Terms"
              : requestType === "intial_terms_request"
                ? "Send Counter Request"
                : "Send Terms Request"}
        </Button>
        <Button
          type="text"
          className="messages-terms-cancel-button"
          onClick={resetTermsForm}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};
export default TermsRequestPanel;
